import ChevronDownIcon from '@heroicons/react/16/solid/ChevronDownIcon'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import useAppStore from '@/store/useAppStore'
import useVaultSelection from '@/pages/dashboard/useVaultSelection'
import useGetCashflowPeriods from '@/services/api/cash-flow/useGetCashflowPeriods'
import { swatchColor } from '@/pages/dashboard/vaultColor'
import { formatMoney } from '@/pages/dashboard/moneyFormat'

export default function VaultSelectionPopover() {
  const {
    vaultsFlat,
    selectedVaults,
    isSelected,
    toggleVault,
    setSelectedVaultIds,
    splitByVault,
    setSplitByVault,
  } = useVaultSelection()

  const offset = useAppStore((state) => state.offset)
  const numberOfPeriods = useAppStore((state) => state.numberOfPeriods)
  const periodType = useAppStore((state) => state.periodType)
  // Reuses the chart's own query (same params, staleTime:Infinity) — no
  // extra request, just the current balance the chart already has.
  const { data: periods } = useGetCashflowPeriods({ offset, numberOfPeriods, periodType })
  const lastColumn = (periods ?? []).slice().sort((a, b) => a.offset - b.offset).at(-1)

  // Counts only vaults that still exist — a stale id from an applied saved
  // view (an archived/deleted vault) never inflates this past the real total.
  const selectedCount = selectedVaults.length

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 whitespace-nowrap rounded-lg border border-primary bg-white px-2.5 py-1.5 text-sm font-semibold text-txt-primary"
        >
          Vaults{' '}
          <span className="text-txt-accent">
            {selectedCount} of {vaultsFlat.length}
          </span>
          <ChevronDownIcon className="size-3.5 text-txt-secondary" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-[280px] p-2" align="start">
        <div className="flex items-center justify-between px-1 pb-2 text-xs text-txt-secondary">
          <span>
            {vaultsFlat.length} vault{vaultsFlat.length === 1 ? '' : 's'} ·{' '}
            {selectedCount} shown
          </span>
          <div className="flex gap-2.5 font-semibold text-txt-accent">
            <button type="button" onClick={() => setSelectedVaultIds(null)}>
              All
            </button>
            <button type="button" onClick={() => setSelectedVaultIds([])}>
              None
            </button>
          </div>
        </div>

        <div className="flex max-h-64 flex-col gap-0.5 overflow-y-auto">
          {vaultsFlat.map((vault) => (
            <label
              key={vault.id}
              className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1.5 hover:bg-gray-tr-08"
            >
              <Checkbox
                checked={isSelected(vault.id)}
                onCheckedChange={(checked) => toggleVault(vault.id, !!checked)}
              />
              <span
                className="h-[3px] w-4 shrink-0 rounded-sm"
                style={{ background: swatchColor(vault.id) }}
              />
              <span className="flex-1 truncate text-sm text-txt-primary">
                {vault.name}
              </span>
              {lastColumn?.vaults?.[vault.id]?.closing_balance !== undefined && (
                <span className="shrink-0 tabular-nums text-xs text-txt-secondary">
                  {formatMoney(lastColumn.vaults[vault.id].closing_balance)}
                </span>
              )}
            </label>
          ))}
        </div>

        <label className="mt-1 flex cursor-pointer items-center gap-2 border-t border-primary pt-2">
          <Switch checked={splitByVault} onCheckedChange={setSplitByVault} />
          <span className="text-sm text-txt-secondary">
            Show a line per vault
          </span>
        </label>
      </PopoverContent>
    </Popover>
  )
}
